import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import MetaHead from "./MetaHead";
import { useTranslation } from "react-i18next";
import { trackPageVisit } from "../utils/analytics";
import Footer from "./Footer";
import SocialHandles from "./SocialHandles";

const FAQ: React.FC = () => {
  const { t } = useTranslation();

  useEffect(() => {

    trackPageVisit("faq");
  }, []);

  return (
    <>
      <MetaHead
        title={t("faq.title")}
        description={t("faq.description") as string}
        keywords={t("faq.keywords") as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 animate-fade-in">
        {/* Navbar */}
        <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm border-b border-neutral-200 dark:border-neutral-700 sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                </div>
                <span className="text-xl font-bold bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
                  Streak Spark
                </span>
              </Link>
              <div className="flex items-center space-x-4">
                <Link
                  to="/"
                  className="text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-300"
                >
                  {t("faq.home")}
                </Link>
                <Link
                  to="/app"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 py-2 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  {t("faq.openApp")}
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-4xl mx-auto px-4 py-8 text-center">
          {/* Hero Section */}
          <div className="mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
              <svg
                className="w-8 h-8 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-primary-600 via-accent-600 to-secondary-600 bg-clip-text text-transparent mb-4 leading-tight">
              {t("faq.faqTitle")}
            </h1>

            <p className="text-lg md:text-xl text-neutral-600 dark:text-neutral-300 mb-6 max-w-3xl mx-auto leading-relaxed">
              {t("faq.faqDescription")}
            </p>
          </div>

          {/* FAQ Content Section */}
          <div className="glass-card p-8 rounded-2xl mb-8">
            <div className="space-y-8 text-left">
              {/* FAQ 1 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">1</span>
                  {t("faq.q1")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a1")}
                </p>
              </div>

              {/* FAQ 2 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">2</span>
                  {t("faq.q2")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a2")}
                </p>
              </div>

              {/* FAQ 3 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">3</span>
                  {t("faq.q3")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a3")}
                </p>
              </div>

              {/* FAQ 4 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">4</span>
                  {t("faq.q4")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a4")}
                </p>
              </div>

              {/* FAQ 5 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">5</span>
                  {t("faq.q5")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a5")}
                </p>
              </div>

              {/* FAQ 6 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">6</span>
                  {t("faq.q6")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a6")}
                </p>
              </div>

              {/* FAQ 7 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">7</span>
                  {t("faq.q7")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a7")}
                </p>
              </div>

              {/* FAQ 8 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">8</span>
                  {t("faq.q8")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a8")}
                </p>
              </div>

              {/* FAQ 9 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">9</span>
                  {t("faq.q9")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a9")}
                </p>
              </div>

              {/* FAQ 10 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">10</span>
                  {t("faq.q10")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a10")}
                </p>
              </div>

              {/* FAQ 11 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">11</span>
                  {t("faq.q11")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a11")}
                </p>
              </div>

              {/* FAQ 12 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">12</span>
                  {t("faq.q12")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a12")}
                </p>
              </div>

              {/* FAQ 13 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">13</span>
                  {t("faq.q13")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a13")}
                </p>
              </div>

              {/* FAQ 14 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">14</span>
                  {t("faq.q14")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a14")}
                </p>
              </div>

              {/* FAQ 15 */}
              <div className="border-b border-neutral-200 dark:border-neutral-700 pb-6 last:border-b-0">
                <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-3">
                  <span className="inline-flex items-center justify-center w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-600 text-white text-sm font-bold rounded-full mr-3">15</span>
                  {t("faq.q15")}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-300 leading-relaxed">
                  {t("faq.a15")}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="glass-card p-8 rounded-2xl bg-gradient-to-br from-primary-50/50 to-accent-50/50 dark:from-primary-900/20 dark:to-accent-900/20 border-2 border-primary-200/50 dark:border-primary-800/50">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-secondary-500 to-accent-500 rounded-2xl shadow-glow mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>

              <h2 className="text-2xl font-bold bg-gradient-to-r from-secondary-600 to-accent-600 bg-clip-text text-transparent mb-4">
                {t("faq.contactTitle")}
              </h2>

              <p className="text-lg text-neutral-700 dark:text-neutral-300 mb-6 leading-relaxed">
                {t("faq.contactDescription")}
              </p>

              <div className="bg-white/60 dark:bg-neutral-800/60 rounded-xl p-6 mb-6 border border-neutral-200/50 dark:border-neutral-700/50">
                <p className="text-neutral-800 dark:text-neutral-200 font-medium mb-4">
                  {t("faq.contactInfo")}
                </p>

                <a
                  href="mailto:streakspark7@gmail.com"
                  className="inline-flex items-center space-x-2 bg-gradient-to-r from-secondary-500 to-accent-500 text-white px-6 py-3 rounded-xl font-semibold hover:shadow-glow transition-all duration-300 hover:-translate-y-0.5"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  <span>{t("faq.contactEmail")}</span>
                </a>
              </div>

              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {t("faq.contactFooter")}
              </p>
            </div>
          </div>
        </div>
        <SocialHandles />
        <Footer />
      </div>
    </>
  );
};

export default FAQ;
